import React, { useState } from "react";
import Paywall from "../../common/Paywall";
import { fetchAtlasMatches } from "./athleteServices";
import { fmtMoney } from "./athleteUtils";
import {
  Bar,
  CategoryTile,
  DEFAULT_AVATAR,
  EngagementBenchmark,
  InfoDot,
  LegendBadge,
  MatchTile,
  MetaChips,
  PlatformTile,
  PlayGroupSwitch,
  PlayerRadar,
  ProfileCardShell,
  ProfileInsight,
  RangeMeter,
  RingScore,
  SeasonChart,
  SectionTitle,
  ShootingRow,
  StatMetric,
  StatTile,
  StatsProvenance,
  TeamChips,
  TeamComparison,
  ValueContextCard,
  ViewTab,
  faceCrop,
} from "./profileSections";
import { profileNarratives, profileSubtitle } from "./profileNarratives";
import ProfileTabsMobile from "./mobile/ProfileTabsMobile";
import AthletePerformance from "./performance/AthletePerformance";
import useAthletePerformance from "./performance/useAthletePerformance";
import { hasPerformanceV2, performanceAttributed } from "./performance/athletePerformanceModel";
import { DESKTOP_PROFILE_TABS, DESKTOP_TABS_LABEL, desktopTabFor } from "./performance/profileTabs";
import PerformanceAttribution from "../../common/PerformanceAttribution";
import { EmptyState, Overline, Select } from "../../ForBrands/ui/BrandUI";
import SubTabBar, { SubTabPanel } from "../../ForBrands/ui/SubTabBar";
import { usePhoneTheme } from "../../ForBrands/useIsPhone";
import iconMail from "../../../assets/brand/icon-mail.svg";

// The V2 profile sections under the page header, shared by the athlete's
// own My Profile (AthleteProfilePage, selfView) and the read-only profile
// coaches, reps, brands and staff open (AthleteProfileView). Both render
// the same sections in the same order from the same profile model; the
// athlete's own page speaks in the second person and carries the edit
// shortcuts, the viewer's reads in the third person with no self actions.
// The copy that depends on the reader comes from profileNarratives.js.
//
// premium    entitlements that unlock the valuation cards (Paywall)
// onInfo     opens the CacheValue in context modal; no (i) dots without it,
//            nor on a profile with no context to open (model.context, D3)
// onEdit     (section) => opens the edit wizard; self only
// onManageCircle  opens My Circle; self only
// overviewHeader  phones only: the identity row and actions the page puts
//            at the top of the Overview tab (it draws no PageHeader there)
// tab, onTabChange  desktop only: the open tab (performance, visibility,
//            value) and its change; the pages keep it in ?tab=
//            (performance/profileTabs useProfileTabParam). Without
//            onTabChange (no router: the pages' setter is null) the body
//            keeps its own, starting on `tab`.
//
// Inside the phone theme (Athlete Mode, and Coach Mode's athlete profile)
// below 640px the same data renders as five tabs (mobile/ProfileTabsMobile,
// Figma 2000:373 to 2000:740). Everywhere else, tablets and desktops
// included (and Rep, Brand and the Player Index on phones), the page shows
// the header blocks (chips, photo, CacheScore, Contributions, the valuation
// row, the NILx rate card, Profile + Circle), then a tab bar (Jayson's
// split, spec B1): Performance (default), Visibility and Value, one panel
// at a time. Performance draws Jayson's cards when the profile has them
// (performanceV2, loaded lazily by useAthletePerformance), else the
// profile's own sections, and ends with the data attribution line over the
// league feed's data (common/PerformanceAttribution, Kobi 10/21);
// Visibility the footprint; Value the CacheValue in context and the brand
// alignment.

// Kept here for the pages that import it with ProfileBody.
export { profileSubtitle };

const TABS_ID = "profile-views";

// Brand-fit matches are server-cached weekly; while a run is computing we
// poll a few times so the tiles fill in as soon as matches land. Reports
// null (still matching), the matches, or [] (none / unavailable). Returns
// the cancel function for an effect cleanup.
export const watchBrandMatches = (email, onMatches) => {
  let cancelled = false;
  let timer = null;
  onMatches(null);
  const poll = (attempt) => {
    fetchAtlasMatches(email)
      .then((d) => {
        if (cancelled) return;
        if ((d.matches || []).length) onMatches(d.matches);
        if (d.computing && attempt < 6) timer = setTimeout(() => poll(attempt + 1), 15000);
        else if (!(d.matches || []).length) onMatches([]);
      })
      .catch(() => !cancelled && onMatches([]));
  };
  poll(0);
  return () => {
    cancelled = true;
    clearTimeout(timer);
  };
};

// (i) next to a section title; nothing without onInfo. Every (i) opens the
// CacheValue in context modal, so none shows on a profile without that
// context (a PerformanceValue profile, a profile with no valuation yet).
const infoFor = (onInfo) => (label) => (onInfo ? <InfoDot onClick={onInfo} label={label} /> : null);
const contextInfo = (model, onInfo) => (model?.context ? onInfo : null);

// The profile's one value card (D3: CacheValue, or PerformanceValue in its
// slot for an athlete who has not onboarded) and CacheNILx, behind the one
// paywall, then the NILx rate card: above the tabs.
const ValueSections = ({ model, narratives, premium, onInfo }) => {
  const { tierLabel } = narratives;
  const info = infoFor(onInfo);
  const card = model.valueCard;
  return (
    <>
      {/* Row 2: the value card · CacheNILx, behind the one paywall */}
      <Paywall requiredEntitlements={premium} title={card.paywallTitle} description="Upgrade to view market valuation and social influence value." showPreview showLockIcon={false}>
        <div className="grid gap-4 md:grid-cols-2">
          <ProfileCardShell>
            <SectionTitle title={card.title} subtitle={card.subtitle} right={card.kind === "cacheValue" ? info("CacheValue in context") : null} />
            <RangeMeter range={card.range} badge={card.badge} badgeTone="brand" />
          </ProfileCardShell>
          <ProfileCardShell>
            <SectionTitle title="CacheNILx™ Value" subtitle="Social influence value" right={info()} />
            <RangeMeter range={model.cacheNilx} badge={tierLabel} badgeTone="passive" />
          </ProfileCardShell>
        </div>
      </Paywall>

      {/* NILx rate card: the tab bar comes after it (and Profile + Circle) */}
      {model.nilRates.length > 0 && (
        <Paywall requiredEntitlements={premium} title="Unlock the NILx™ Rate Card" description="Upgrade to view the full NIL rate card." showPreview showLockIcon={false}>
          <ProfileCardShell>
            <SectionTitle title="NILx™ Rate Card" subtitle="NIL rate card" right={info()} />
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {model.nilRates.map((r) => (
                <div key={r.label} className="flex flex-col gap-1 rounded-[8px] border border-bm-border bg-white px-4 py-[14px]">
                  <span className="bm-body-s text-bm-muted">{r.label}</span>
                  <span className="bm-num-m text-bm-ink">{fmtMoney(r.value)}</span>
                </div>
              ))}
            </div>
          </ProfileCardShell>
        </Paywall>
      )}
    </>
  );
};

// CacheValue in context, the Value tab's first card.
const ValueContextSection = ({ model, narratives, premium }) => {
  const { self } = narratives;
  if (!model.context) return null;
  return (
    <Paywall
      requiredEntitlements={premium}
      title={self ? "Unlock your CacheValue™ in context" : "Unlock CacheValue™ in context"}
      description={self ? "Upgrade to see where your valuation sits nationally." : "Upgrade to see where this valuation sits nationally."}
      showPreview
      showLockIcon={false}
    >
      {self ? (
        <ValueContextCard model={model} framed selfView />
      ) : (
        <ProfileCardShell padding="p-7">
          <SectionTitle title="CacheValue™ in context" />
          <ValueContextCard model={model} selfView={false} />
        </ProfileCardShell>
      )}
    </Paywall>
  );
};

// Current season: the year picker's season (an academic year) with a line
// per competition (a camp's tagged), its numbers (a note when they leave
// camp games out) and its chart (per game, else per event, each point's
// popover naming the game or event, camp games hollow), against the peer
// group's average or against teammates when they cover that season, then
// the comparison rows and the outlook.
const CurrentSeasonCard = ({ model, narratives }) => {
  const [picked, setPicked] = useState(null);
  const [seasonView, setSeasonView] = useState("mine");
  const { self, cs, seasonAi, seasonFor } = narratives;
  const { season, subtitle, series, comparison, teammates, note } = seasonFor(picked);
  // A season the teammates do not cover has no teammates view.
  const view = teammates ? seasonView : "mine";
  const you = self ? "You" : cs.firstName || model.firstName || "Athlete";
  const name = cs.firstName || (self ? "You" : "Athlete");
  return (
    <ProfileCardShell padding="p-7" className="!gap-3">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <SectionTitle title="Current season" subtitle={subtitle} />
        <Select
          aria-label="Select year"
          value={season.label}
          onChange={(e) => setPicked(e.target.value)}
          className="!h-12 !w-[226px] !rounded-full !border-bm-strong !pl-5 !pr-10 bm-body-m !text-bm-muted"
        >
          {cs.seasons.map((s) => (
            <option key={s.label} value={s.label}>
              {s.label}
            </option>
          ))}
        </Select>
      </div>
      {season.competitions.length > 0 && (
        <ul aria-label="Competitions" className="flex flex-col gap-1">
          {season.competitions.map((line, i) => (
            <li key={`${line}-${i}`} className="bm-body-m text-bm-ink">
              {line}
            </li>
          ))}
        </ul>
      )}
      <div className="grid grid-cols-2 gap-[10px] pt-2 sm:grid-cols-4">
        {season.numbers.map((n) => (
          <StatTile key={n.label} value={n.value} label={n.label} />
        ))}
      </div>
      {season.campNote && <p className="bm-body-s text-bm-muted">{season.campNote}</p>}
      {teammates && (
        <>
          <div className="pt-3"><Overline>Athlete view</Overline></div>
          <div className="flex items-start gap-[10px]">
            <ViewTab active={view === "mine"} onClick={() => setSeasonView("mine")}>{self ? "My stats" : "Season stats"}</ViewTab>
            <ViewTab active={view === "teammates"} onClick={() => setSeasonView("teammates")}>vs. teammates</ViewTab>
          </div>
        </>
      )}
      {view === "mine" && series.length > 0 && (
        <>
          <div className="flex items-center gap-[26px] pb-[2px] pt-[10px]">
            <LegendBadge color="#FF4400" label={`${name} (PPG)`} />
            {comparison?.avgPpg != null && <LegendBadge color="#b8b8bc" label={comparison.avgName} />}
            {season.campLegend && <LegendBadge color="#FF4400" hollow label={season.campLegend} />}
          </div>
          <SeasonChart
            key={season.label}
            series={series}
            avgName={comparison?.avgName}
            name={name}
            unit={season.kind === "games" ? "points" : "PPG"}
          />
        </>
      )}
      {view === "teammates" && (
        <>
          <div className="flex items-center gap-[26px] pb-[2px] pt-[10px]">
            <LegendBadge color="#FF4400" label={you} />
            <LegendBadge color="#d9d9d9" label="Team avg" />
          </div>
          <TeamComparison teammates={teammates} label={you} />
        </>
      )}
      {comparison && (
        <>
          {comparison.caption && <p className="pt-[10px] bm-body-s text-bm-muted">{comparison.caption}</p>}
          {comparison.tiles.length > 0 && (
            <div className="flex flex-col gap-[10px] pt-[10px] sm:flex-row">
              {comparison.tiles.map((t) => (
                <StatMetric key={t.label} label={t.label} value={t.value} chip={t.chip} />
              ))}
            </div>
          )}
        </>
      )}
      {note && <p className="pt-[10px] bm-body-s text-bm-muted">{note}</p>}
      {seasonAi && <ProfileInsight label={self ? "Keep improving" : "Season outlook"} className="mt-3">{seasonAi}</ProfileInsight>}
    </ProfileCardShell>
  );
};

// The sport tab, the season box score (shooting efficiency, the player
// radar with its League / College / Pro groups, and the season summary)
// and the current season. No level draws the shot zone chart: its dots
// were generated, not real shots. With two or more play groups a School |
// Grassroots switch under the box score's header picks the line the tiles,
// the shooting rows and the summary read (D7); the radar keeps its own
// group, which its caption names.
const PerformanceSections = ({ model, narratives }) => {
  const [picked, setPicked] = useState(null);
  const { self, cs, showStats } = narratives;
  const line = narratives.boxLine ? narratives.boxLine(picked) : model;
  const subtitle = narratives.boxScoreSubtitleFor ? narratives.boxScoreSubtitleFor(line) : narratives.boxScoreSubtitle;
  const performanceSummary = narratives.performanceSummaryFor ? narratives.performanceSummaryFor(line) : narratives.performanceSummary;
  return (
    <>
      {/* Sport tab: the stats below are always for this sport */}
      {model.sport && showStats && (
        <div className="py-[6px]">
          <span className="inline-flex h-12 items-center rounded-full bg-bm-ink px-6 bm-label-l text-white">{model.sport}</span>
        </div>
      )}

      {/* Season box score */}
      <ProfileCardShell padding="p-7" className="!gap-3">
        <SectionTitle title="Season box score" subtitle={subtitle} />
        <PlayGroupSwitch groups={model.playGroups} value={line.key} onChange={setPicked} />
        <StatsProvenance model={model} />
        {line.hasBoxScore ? (
          <>
            <div className="grid grid-cols-3 gap-[10px] md:grid-cols-5 xl:grid-cols-9">
              {line.boxScore.map((s) => (
                <StatTile key={s.label} value={s.value} label={s.label} chips={s.chips} />
              ))}
            </div>
            <div className="flex flex-col gap-8 pt-9 lg:flex-row">
              <div className="flex min-w-0 flex-1 flex-col gap-[10px]">
                <div className="flex flex-col gap-3">
                  <span className="bm-h-s text-bm-ink">Shooting efficiency</span>
                  <span className="bm-body-s text-bm-muted">KenPom-style efficiency metrics, how well, not just how much</span>
                </div>
                {line.shooting.length ? (
                  line.shooting.map((s) => <ShootingRow key={s.label} item={s} />)
                ) : (
                  <p className="bm-body-s text-bm-muted">Efficiency metrics appear once shot attempts are on file.</p>
                )}
                {/* The ranks beside the numbers: the same-state subset (blue, when one shows) and the whole peer group (grey); only on a line that has them. */}
                {line.hasPercentiles && (
                  <div className="flex items-center gap-3">
                    {line.hasStateRanks && <LegendBadge color="#2196f3" label="State" />}
                    <LegendBadge color="#757575" label="Peer group" />
                  </div>
                )}
              </div>
              <div className="flex w-full shrink-0 flex-col gap-9 lg:w-[342px]">
                <PlayerRadar groups={model.radarGroups} />
              </div>
            </div>
            {performanceSummary && (
              <div className="pt-9">
                <ProfileInsight label={self ? "Your season summary" : "Season summary"}>{performanceSummary}</ProfileInsight>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-[10px] md:grid-cols-5 xl:grid-cols-9">
              {line.boxScore.map((s) => (
                <StatTile key={s.label} value={null} label={s.label} />
              ))}
            </div>
            <p className="bm-body-m text-bm-muted">
              {self
                ? "No season stats on file yet. Stats sync from your program's data partner once your coach verifies you, or add them under Edit my profile."
                : "No season stats on file yet. Stats appear once they sync from the athlete's program data partner."}
            </p>
          </>
        )}
      </ProfileCardShell>

      {/* Current season */}
      {cs && <CurrentSeasonCard model={model} narratives={narratives} />}
    </>
  );
};

// Brand category alignment: matched sponsors, or the audience-derived
// categories, beside the brand alignment read from the athlete's interests,
// NIL deals and work experience.
const BrandAlignmentSection = ({ model, narratives, matches }) => {
  const { self, hasMatches, matching, brandNarrative, brandSubtitle } = narratives;
  if (!(model.brand || matching || hasMatches)) return null;
  return (
    <ProfileCardShell padding="p-7" className="!gap-6">
      <SectionTitle title="Brand category alignment" subtitle={brandSubtitle} />
      {matching && (
        <span className="flex items-center gap-3 bm-body-s text-bm-muted">
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-bm-border border-t-bm-orange" />
          {self ? "Matching brands to your profile…" : "Matching brands to this profile…"}
        </span>
      )}
      <div className="flex flex-col gap-12 lg:flex-row lg:items-stretch">
        <div className="grid min-w-0 flex-1 grid-cols-2 gap-[10px] md:grid-cols-3">
          {hasMatches
            ? matches.slice(0, 6).map((m) => <MatchTile key={m.domain || m.brand} match={m} selfView={self} />)
            : (model.brand?.categories || []).map((c) => <CategoryTile key={c.mono} category={c} />)}
        </div>
        <ProfileInsight label="Brand alignment" subtitle="From interests, NIL deals and work experience" className="w-full shrink-0 lg:w-[300px]">
          {brandNarrative}
        </ProfileInsight>
      </div>
    </ProfileCardShell>
  );
};

// Visibility footprint: the platforms, the engagement benchmark and the
// visibility read (the Visibility tab).
const VisibilitySection = ({ model, narratives, onEdit }) => {
  const { self, engagement, visibilityNarrative, visibilityTitle } = narratives;
  return (
    <ProfileCardShell padding="p-7" className="!gap-[14px]">
      <SectionTitle title="Visibility footprint" subtitle="Reach and engagement across platforms" />
      <div className="flex flex-col gap-12 lg:flex-row lg:items-stretch">
        <div className="flex min-w-0 flex-1 flex-col gap-[14px]">
          <div className="grid gap-[10px] sm:grid-cols-2 xl:grid-cols-4">
            {model.platforms.map((p) => (
              <PlatformTile key={p.key} platform={p} onAdd={onEdit ? () => onEdit("socials") : undefined} />
            ))}
          </div>
          <EngagementBenchmark engagement={engagement} selfView={self} />
        </div>
        <ProfileInsight label={visibilityTitle} className="w-full shrink-0 lg:w-[300px]">
          {visibilityNarrative}
        </ProfileInsight>
      </div>
    </ProfileCardShell>
  );
};

// The Value tab: CacheValue in context and the brand category alignment.
const ValueTabSections = ({ model, narratives, premium, matches }) => {
  const { self, hasMatches, matching } = narratives;
  const brand = Boolean(model.brand || matching || hasMatches);
  if (!model.context && !brand) {
    // A PerformanceValue profile has no CacheValue in context to wait for.
    const performance = model.valueBasis === "performanceValue";
    return (
      <EmptyState>
        {performance
          ? self
            ? "Your brand alignment appears once your profile has enough data."
            : "The brand alignment appears once this profile has enough data."
          : self
            ? "Your valuation in context and brand alignment appear once your profile has enough data."
            : "The valuation in context and brand alignment appear once this profile has enough data."}
      </EmptyState>
    );
  }
  return (
    <>
      <ValueContextSection model={model} narratives={narratives} premium={premium} />
      <BrandAlignmentSection model={model} narratives={narratives} matches={matches} />
    </>
  );
};

const ProfileBody = ({
  model,
  athlete = {},
  cacheInformation = {},
  matches,
  premium,
  selfView = false,
  onInfo,
  onEdit,
  onManageCircle,
  overviewHeader,
  tab: tabProp,
  onTabChange,
}) => {
  const phone = usePhoneTheme();
  // The page's tab when it keeps one; else the body's own, so the tabs
  // still switch where a page has no router to keep ?tab= in.
  const controlled = tabProp != null && typeof onTabChange === "function";
  const [ownTab, setOwnTab] = useState(() => desktopTabFor(tabProp));
  const tab = controlled ? desktopTabFor(tabProp) : ownTab;
  const performanceV2 = hasPerformanceV2(model);
  // Jayson's cards load when the desktop Performance tab is open (phones
  // load them in ProfileTabsMobile), once per athlete and season.
  const perf = useAthletePerformance({
    athleteId: model.athleteId,
    email: athlete?.email || cacheInformation?.athleteEmail,
    enabled: !phone && performanceV2 && tab === "performance",
  });
  const narratives = profileNarratives({ model, athlete, cacheInformation, matches, selfView });
  const { self } = narratives;

  if (phone) {
    return (
      <ProfileTabsMobile
        model={model}
        athlete={athlete}
        narratives={narratives}
        matches={matches}
        premium={premium}
        selfView={self}
        onEdit={onEdit}
        overviewHeader={overviewHeader}
      />
    );
  }

  // The pages pass onInfo only with a CacheValue in context to open; the
  // body holds to the same rule for any caller.
  const openContext = contextInfo(model, onInfo);
  const info = infoFor(openContext);
  const selectTab = (next) => {
    const key = desktopTabFor(next);
    setOwnTab(key);
    onTabChange?.(key);
  };
  const performance = <PerformanceSections model={model} narratives={narratives} />;

  return (
    <div className="flex flex-col gap-4 pt-2">
      <MetaChips chips={model.metaChips} />
      <TeamChips teams={athlete.allTeams} />

      {/* Row 1: photo · CacheScore · Contributions */}
      <div className="grid gap-4 md:grid-cols-[minmax(200px,260px)_1fr_1fr]">
        <div className="overflow-hidden rounded-[12px] border border-bm-border bg-bm-mutedbg">
          <img src={faceCrop(model.avatar || DEFAULT_AVATAR)} alt={model.fullName} className="h-full min-h-[240px] w-full object-cover object-top" />
        </div>
        <ProfileCardShell>
          <SectionTitle title="CacheScore™" subtitle="Overall brand strength indicator" right={info()} />
          <div className="flex flex-1 items-center justify-center">
            <RingScore value={model.cacheScore} confidence={model.confidence} />
          </div>
        </ProfileCardShell>
        <ProfileCardShell>
          <SectionTitle title="Contributions™" subtitle="Performance, visibility & influence breakdown" right={info()} />
          <div className="flex flex-1 flex-col justify-center gap-5">
            <Bar label="Performance" value={model.contributions.performance} />
            <Bar label="Visibility" value={model.contributions.visibility} />
            <Bar label="Off-court impact" value={model.contributions.offCourt} />
          </div>
        </ProfileCardShell>
      </div>

      <ValueSections model={model} narratives={narratives} premium={premium} onInfo={openContext} />

      {/* Profile + circle */}
      <ProfileCardShell>
        <SectionTitle title="Profile" />
        <div className="grid gap-6 lg:grid-cols-[1fr_300px]">
          <div className="flex flex-col gap-5">
            <div className="flex flex-col gap-2">
              <Overline>Bio</Overline>
              {model.bio ? (
                <p className="border-l-2 border-bm-orange pl-4 bm-body-m text-bm-muted">{model.bio}</p>
              ) : onEdit ? (
                <p className="bm-body-m text-bm-muted">
                  No bio yet.{" "}
                  <button type="button" onClick={() => onEdit("photo")} className="text-bm-orange underline underline-offset-2">
                    Add one
                  </button>{" "}
                  so brands know your story.
                </p>
              ) : (
                <p className="bm-body-m text-bm-muted">No bio yet.</p>
              )}
            </div>
            {model.profileFields.length > 0 ? (
              <div className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
                {model.profileFields.map((f) => (
                  <div key={f.label} className="flex flex-col gap-[2px] border-b border-bm-mutedbg pb-3">
                    <Overline>{f.label}</Overline>
                    <span className="bm-label-l text-bm-ink">
                      {f.value}
                      {f.unit ? <span className="bm-body-s text-bm-muted"> {f.unit}</span> : null}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="bm-body-m text-bm-muted">No profile details yet.</p>
            )}
          </div>
          <div className="flex flex-col gap-3 rounded-[8px] bg-bm-subtle p-4">
            <div className="flex items-center justify-between">
              <span className="bm-h-xs text-bm-ink">Circle</span>
              {onManageCircle && (
                <button type="button" onClick={onManageCircle} className="bm-label-xs text-bm-orange">
                  Manage
                </button>
              )}
            </div>
            {model.circle.length === 0 && (
              <p className="bm-body-s text-bm-muted">
                {self ? "No circle members added yet. Add your rep, guardian and coaches in My Circle." : "No circle members added yet."}
              </p>
            )}
            {model.circle.map((m, i) => (
              <div key={`${m.role}-${m.name}-${i}`} className="flex items-center justify-between gap-2 rounded-[8px] border border-bm-border bg-white px-3 py-2">
                <span className="flex min-w-0 flex-col">
                  <Overline>{m.role}</Overline>
                  <span className="truncate bm-label-l text-bm-ink">{m.name}</span>
                </span>
                {m.email && (
                  <a href={`mailto:${m.email}`} aria-label={`Email ${m.name}`} className="opacity-60 hover:opacity-100">
                    <img src={iconMail} alt="" className="h-4 w-4" />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </ProfileCardShell>

      {/* Performance · Visibility · Value: one panel at a time */}
      <div className="flex flex-col gap-4 pt-2">
        <SubTabBar tabs={DESKTOP_PROFILE_TABS} value={tab} onChange={selectTab} ariaLabel={DESKTOP_TABS_LABEL} idPrefix={TABS_ID} />
        <SubTabPanel
          idPrefix={TABS_ID}
          tabKey={tab}
          className="flex flex-col gap-4 rounded-[12px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-bm-ink"
        >
          {tab === "performance" &&
            (performanceV2 ? <AthletePerformance perf={perf} model={model} narratives={narratives} fallback={performance} /> : performance)}
          {/* The data attribution closes the tab, once, over the league feed's data only (Kobi, 10/21). */}
          {tab === "performance" && performanceAttributed({ model, perf }) && <PerformanceAttribution />}
          {tab === "visibility" && <VisibilitySection model={model} narratives={narratives} onEdit={onEdit} />}
          {tab === "value" && <ValueTabSections model={model} narratives={narratives} premium={premium} matches={matches} />}
        </SubTabPanel>
        {/* Only the open panel is on the page: SubTabBar points the selected
            tab alone at it (aria-controls). */}
      </div>
    </div>
  );
};

export default ProfileBody;
